import { Mui, useTheme, alpha } from "@leap4/frontend";
import React, { useEffect, useMemo, useRef, useState } from "react";

const { Box, Stack, Typography } = Mui;

export interface IChartPoint {

    label: string;

    /** Fuller form of `label` for the tooltip heading, where there is room for an unabbreviated date. */
    tooltipLabel?: string;

    /** Shown beside the value in the tooltip. Used for the margin of error on a predicted point. */
    note?: string;

    /** Colours the note. Set where the note carries a judgement, so it reads without being parsed. */
    noteColor?: string;

    /**
     * Drawn, but not reported: no tooltip row and no hover dot. For a point a series only carries so its
     * path joins its neighbour's — reporting it would assert the series covers a date it does not.
     */
    silent?: boolean;

    value: number | null;

    major?: boolean;

    /** Optional confidence bounds. Drawn as a shaded band, so a wide interval reads as low trust. */
    upper?: number | null;
    lower?: number | null;
}

export interface IChartSeries {
    id: string;
    label: string;
    color: string;

    points: IChartPoint[];
    dashed?: boolean;

    /**
     * Which vertical scale to draw against. Series whose magnitudes differ by orders of magnitude
     * cannot share one axis — the smaller flattens onto zero and reads as no data.
     */
    axis?: "left" | "right";

    /**
     * Drawn dashed at full weight, with its band doing the work of showing uncertainty. Deliberately
     * not faint: a line thin enough to look uncertain is also thin enough to look absent, and an
     * invisible line reads as a bug rather than as a caveat.
     */
    tentative?: boolean;

    /**
     * Colour the stroke emerges from over its first stretch. Set on a series that continues another —
     * the predicted line leaving the measured one — so the handover reads as one line changing
     * character rather than two lines meeting.
     */
    fadeFrom?: string;

    /**
     * Which lines share a vertical scale while normalised. Defaults to the label, so unrelated series
     * keep their own. Set it where two lines measure the same thing and must be read against each
     * other: a measured and a predicted total near-identical on the day should sit near-identical on
     * the chart, and scaling each to its own maximum put them a third of the plot apart.
     */
    scaleKey?: string;
}

interface IProps {
    series: IChartSeries[];
    height?: number;
    valueFormatter?: (value: number) => string;

    maturingFromIndex?: number | null;

    /**
     * Last index the shading covers. Beyond it the dates have not happened, so nothing is accruing and
     * the dashed stroke already says so. Defaults to the end of the chart.
     */
    maturingToIndex?: number | null;

    maturingNote?: string;

    /**
     * First index that has not happened yet. Washed with a gradient that fades out to the right rather
     * than hatched: a different visual language from "still settling", because it is a different claim.
     */
    futureFromIndex?: number | null;

    futureNote?: string;

    /**
     * Draw each series against its own maximum. For charts mixing units — impressions against
     * revenue against counts — where one shared axis flattens the smaller series onto zero.
     * The vertical axis is unlabelled while on, because no single set of numbers is true for all.
     */
    normalise?: boolean;

    /**
     * Label of the focused line. Focus applies to its whole scale group, so a metric and the
     * prediction drawn against it light up together — they are one reading, not two.
     */
    focusedId?: string | null;

    onFocusChange?: (id: string | null) => void;
}

const MARGIN = { top: 16, right: 56, bottom: 40, left: 64 };

/**
 * The future wash still fades to the right, but only part way: it has to stay clearly present at the
 * far edge, or the region it marks stops looking marked at all.
 */
const FUTURE_WASH_ALPHA = 0.3;
const FUTURE_WASH_FADE = 0.55;

const DEFAULT_FUTURE_NOTE =
    "Yet to happen — no impressions have been served for these dates, so the line is a forecast from past performance rather than a reading.";

const DEFAULT_MATURING_NOTE =
    "Data maturing window — recent data still falls inside the attribution window and will keep growing as more events are attributed.";

const defaultFormatter = (value: number): string => {
    if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
    if (Math.abs(value) >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
    return Number.isInteger(value) ? value.toLocaleString() : value.toFixed(2);
};

const LineChart = ({
    series,
    height = 320,
    valueFormatter = defaultFormatter,
    maturingFromIndex = null,
    maturingToIndex = null,
    maturingNote = DEFAULT_MATURING_NOTE,
    futureFromIndex = null,
    futureNote = DEFAULT_FUTURE_NOTE,
    normalise = false,
    focusedId = null,
    onFocusChange,
}: IProps): JSX.Element => {
    const theme = useTheme();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [hoverIndex, setHoverIndex] = useState<number | null>(null);
    const [containerWidth, setContainerWidth] = useState(0);

    const hatchId = useRef(`maturing-hatch-${Math.random().toString(36).slice(2)}`).current;
    const gradientId = useRef(`series-fade-${Math.random().toString(36).slice(2)}`).current;
    const futureId = useRef(`future-wash-${Math.random().toString(36).slice(2)}`).current;

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return undefined;
        setContainerWidth(el.getBoundingClientRect().width);
        if (typeof ResizeObserver === "undefined") return undefined;
        const observer = new ResizeObserver((entries) => {
            setContainerWidth(entries[0]?.contentRect.width ?? 0);
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const viewWidth = containerWidth;
    const isNarrow = viewWidth > 0 && viewWidth < 520;
    const fontFamily = theme.typography.fontFamily;
    const axisFontSize = isNarrow ? 11 : 13;

    const viewHeight = height;
    const plotWidth = Math.max(0, viewWidth - MARGIN.left - MARGIN.right);
    const plotHeight = viewHeight - MARGIN.top - MARGIN.bottom;

    const primary = series[0];
    const pointCount = primary?.points.length ?? 0;

    const maxOfSeries = (s: IChartSeries): number => {
        let max = 0;
        for (const p of s.points) {
            if (p.value != null && p.value > max) max = p.value;
            if (p.upper != null && p.upper > max) max = p.upper;
        }
        return max === 0 ? 1 : max * 1.08;
    };

    const maxFor = (side: "left" | "right"): number => {
        let max = 0;
        for (const s of series) {
            if ((s.axis ?? "left") !== side) continue;
            const m = maxOfSeries(s);
            if (m > max) max = m;
        }
        return max === 0 ? 1 : max;
    };

    const yMax = useMemo(() => maxFor("left"), [series]);
    const yMaxRight = useMemo(() => maxFor("right"), [series]);
    const hasRightAxis = useMemo(
        () => !normalise && series.some((s) => s.axis === "right"),
        [series, normalise],
    );

    /**
     * Keyed by scale group rather than id: one logical line is several series — measured, tentative,
     * forecast — and scaling each by its own maximum would break a continuous line into misaligned
     * fragments. The group is wider than the label wherever a caller says so.
     */
    const scaleKeyOf = (s: IChartSeries): string => s.scaleKey ?? s.label;

    const seriesMax = useMemo(() => {
        const out = new Map<string, number>();
        for (const s of series) {
            const key = scaleKeyOf(s);
            out.set(key, Math.max(out.get(key) ?? 0, maxOfSeries(s)));
        }
        return out;
    }, [series]);

    /** A label names a line; its scale may belong to a wider group, so the two are looked up apart. */
    const scaleOfLabel = useMemo(() => {
        const out = new Map<string, string>();
        for (const s of series) out.set(s.label, scaleKeyOf(s));
        return out;
    }, [series]);

    const focusedScale = focusedId ? scaleOfLabel.get(focusedId) ?? focusedId : null;
    const inFocus = (s: IChartSeries): boolean => !focusedScale || scaleKeyOf(s) === focusedScale;

    const xFor = (index: number): number => {
        if (pointCount <= 1) return MARGIN.left + plotWidth / 2;
        return MARGIN.left + (index / (pointCount - 1)) * plotWidth;
    };
    const yForAxis = (value: number, side: "left" | "right" = "left"): number =>
        MARGIN.top + plotHeight - (value / (side === "right" ? yMaxRight : yMax)) * plotHeight;

    /** Ticks follow whichever scale they are labelling: the shared axis, or the focused series alone. */
    const yForTick = (value: number): number =>
        MARGIN.top + plotHeight - (value / (tickScale || 1)) * plotHeight;

    const yFor = (value: number, s: IChartSeries): number => {
        if (!normalise) return yForAxis(value, s.axis ?? "left");
        return MARGIN.top + plotHeight - (value / (seriesMax.get(scaleKeyOf(s)) ?? 1)) * plotHeight;
    };

    const buildPath = (s: IChartSeries, fromIndex = 0): string => {
        let d = "";
        let penDown = false;
        s.points.forEach((p, i) => {
            if (i < fromIndex || p.value == null) {
                penDown = false;
                return;
            }
            const cmd = penDown ? "L" : "M";
            d += `${cmd}${xFor(i).toFixed(2)},${yFor(p.value, s).toFixed(2)} `;
            penDown = true;
        });
        return d.trim();
    };

    // Upper edge left to right, lower edge back again, closed. Split on gaps so a series with holes
    // does not shade across them.
    const bandPaths = (s: IChartSeries): string[] => {
        const out: string[] = [];
        let run: Array<{ i: number; upper: number; lower: number }> = [];
        const flush = () => {
            if (run.length >= 2) {
                const side = s.axis ?? "left";
                const up = run
                    .map((r, k) => `${k === 0 ? "M" : "L"}${xFor(r.i).toFixed(2)},${yFor(r.upper, s).toFixed(2)}`)
                    .join(" ");
                const down = [...run]
                    .reverse()
                    .map((r) => `L${xFor(r.i).toFixed(2)},${yFor(r.lower, s).toFixed(2)}`)
                    .join(" ");
                out.push(`${up} ${down} Z`);
            }
            run = [];
        };
        s.points.forEach((p, i) => {
            if (p.upper == null || p.lower == null) {
                flush();
                return;
            }
            run.push({ i, upper: p.upper, lower: Math.max(0, p.lower) });
        });
        flush();
        return out;
    };

    // Normalised without a focus has no true set of numbers, so the axis carries none. Focusing a
    // series is what earns the labels back, and they then describe that series alone.
    // focusedId names a label group, since that is what a reader sees as one line.
    const tickScale = normalise ? (focusedScale ? seriesMax.get(focusedScale) ?? 0 : 0) : yMax;

    /**
     * How much of a line survives a focus elsewhere. Dimmed rather than hidden: the shape of what is
     * not focused is still the comparison the chart is for, and removing it changes what is being read.
     */
    const focusOpacity = (s: IChartSeries): number => (inFocus(s) ? 1 : 0.16);

    /**
     * Bands stack. Four events on one chart paint four translucent fills over each other and the plot
     * turns to mush, so the fill is thinned by how many scales carry one. Anything out of focus drops
     * its band entirely rather than fading it — a dimmed fill still accumulates with its neighbours.
     */
    const bandedScales = new Set(
        series
            .filter((s) => s.points.some((p) => p.upper != null && p.lower != null))
            .map((s) => scaleKeyOf(s)),
    ).size;
    const bandAlpha = (s: IChartSeries): number => {
        // Flat across tentative and firm: the band's width already says how uncertain a point is, and
        // varying its density too made one metric's band change shade partway along its own length.
        const base = 0.2;
        if (focusedScale) return inFocus(s) ? base : 0;
        return base / Math.max(1, Math.sqrt(bandedScales));
    };

    // Focused last, so the lines it is being compared against cannot be drawn over it.
    const drawOrder = useMemo(() => {
        const ordered = [...series].reverse();
        if (!focusedScale) return ordered;
        return [...ordered.filter((s) => !inFocus(s)), ...ordered.filter((s) => inFocus(s))];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [series, focusedScale]);

    // One entry per label group, since three series can share a line.
    const labelGroups = useMemo(
        () => Array.from(new Map(series.map((s) => [s.label, s])).values()),
        [series],
    );

    const yTicks = useMemo(() => {
        if (tickScale === 0) return [];
        const count = 4;
        return Array.from({ length: count + 1 }, (_, i) => (tickScale / count) * i);
    }, [tickScale]);

    const xTickIndexes = useMemo(() => {
        if (pointCount === 0) return [];
        const maxTicks = isNarrow ? 4 : 8;
        if (pointCount <= maxTicks) return primary.points.map((_, i) => i);

        const majors = primary.points.reduce<number[]>((acc, p, i) => {
            if (p.major) acc.push(i);
            return acc;
        }, []);
        if (majors.length >= 3) {
            const cap = isNarrow ? 6 : 32;
            if (majors.length <= cap) return majors;
            const step = Math.ceil(majors.length / cap);
            const idxs: number[] = [];
            for (let i = 0; i < majors.length; i += step) idxs.push(majors[i]);
            const last = majors[majors.length - 1];
            if (idxs[idxs.length - 1] !== last) idxs.push(last);
            return idxs;
        }

        const step = Math.ceil(pointCount / maxTicks);
        const idxs: number[] = [];
        for (let i = 0; i < pointCount; i += step) idxs.push(i);
        if (idxs[idxs.length - 1] !== pointCount - 1) idxs.push(pointCount - 1);
        return idxs;
    }, [pointCount, primary, isNarrow]);

    const gridColor = alpha(theme.palette.text.primary, 0.08);
    const axisColor = alpha(theme.palette.text.primary, 0.45);

    const maturingActive =
        maturingFromIndex != null && pointCount > 1 && maturingFromIndex >= 0 && maturingFromIndex <= pointCount - 1;
    const maturingStartIndex = maturingActive
        ? Math.min(maturingFromIndex as number, Math.max(0, pointCount - 2))
        : 0;
    const maturingBandX = maturingActive ? xFor(maturingStartIndex) : 0;
    const maturingBandRight =
        maturingActive && maturingToIndex != null && maturingToIndex > maturingStartIndex
            ? Math.min(viewWidth - MARGIN.right, xFor(Math.min(maturingToIndex, pointCount - 1)))
            : viewWidth - MARGIN.right;
    const futureActive =
        futureFromIndex != null && pointCount > 1 && futureFromIndex >= 0 && futureFromIndex < pointCount - 1;
    const futureBandX = futureActive ? xFor(futureFromIndex as number) : 0;
    const futureBandRight = viewWidth - MARGIN.right;

    const maturingLineColor = alpha(theme.palette.text.primary, 0.45);
    const hatchLineColor = alpha(theme.palette.text.primary, 0.22);
    const hatchFillColor = alpha(theme.palette.text.primary, 0.05);
    const futureWashColor = alpha(theme.palette.primary.light, FUTURE_WASH_ALPHA);
    const futureWashFadedColor = alpha(theme.palette.primary.light, FUTURE_WASH_ALPHA * FUTURE_WASH_FADE);
    const futureEdgeColor = alpha(theme.palette.primary.light, 0.9);

    const handleMove = (event: React.MouseEvent<HTMLDivElement>) => {
        const el = containerRef.current;
        if (!el || pointCount === 0) return;
        const rect = el.getBoundingClientRect();
        const ratio = (event.clientX - rect.left) / rect.width;
        const x = ratio * viewWidth;
        const rel = (x - MARGIN.left) / (plotWidth || 1);
        const idx = Math.round(rel * (pointCount - 1));
        setHoverIndex(Math.max(0, Math.min(pointCount - 1, idx)));
    };

    const hoverLeftPct = hoverIndex != null && viewWidth > 0 ? (xFor(hoverIndex) / viewWidth) * 100 : 0;
    const tooltipRows = useMemo(() => {
        if (hoverIndex == null) return [];
        const reportableAt = (s: IChartSeries): boolean => {
            const p = s.points[hoverIndex as number];
            return p?.value != null && !p.silent;
        };

        const byLabel = new Map<string, IChartSeries>();
        for (const s of series) {
            const existing = byLabel.get(s.label);
            // Prefer whichever member of the group actually reports a value at this point.
            if (!existing || (reportableAt(s) && !reportableAt(existing))) {
                byLabel.set(s.label, s);
            }
        }
        // A series with nothing at this date is not part of the story here: a forecast has no reading
        // in the past and a measurement has none in the future, and listing either as a dash invites
        // the question of why it is missing.
        return Array.from(byLabel.values()).filter(reportableAt);
    }, [series, hoverIndex]);

    const tooltipOnRight = hoverLeftPct < 55;

    return (
        <Box ref={containerRef} sx={{ position: "relative", width: "100%" }} onMouseMove={handleMove} onMouseLeave={() => setHoverIndex(null)}>
            <svg viewBox={`0 0 ${viewWidth} ${viewHeight}`} width="100%" height={viewHeight} role="img">
                <defs>
                    <pattern id={hatchId} width={7} height={7} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                        <rect width={7} height={7} fill={hatchFillColor} />
                        <line x1={0} y1={0} x2={0} y2={7} stroke={hatchLineColor} strokeWidth={1.4} />
                    </pattern>
                    <linearGradient id={futureId} x1="0" x2="1" y1="0" y2="0">
                        <stop offset="0%" stopColor={futureWashColor} stopOpacity={1} />
                        <stop offset="100%" stopColor={futureWashColor} stopOpacity={FUTURE_WASH_FADE} />
                    </linearGradient>
                    {series
                        .filter((s) => s.fadeFrom)
                        .map((s) => (
                            <linearGradient key={`grad-${s.id}`} id={`${gradientId}-${s.id}`} x1="0" x2="1" y1="0" y2="0">
                                <stop offset="0%" stopColor={s.fadeFrom} />
                                <stop offset="18%" stopColor={s.color} />
                            </linearGradient>
                        ))}
                </defs>
                {viewWidth > 0 && (
                    <>
                        {maturingActive && (
                            <g>
                                <rect
                                    x={maturingBandX}
                                    y={MARGIN.top}
                                    width={Math.max(0, maturingBandRight - maturingBandX)}
                                    height={plotHeight}
                                    fill={`url(#${hatchId})`}
                                />
                                <line
                                    x1={maturingBandX}
                                    y1={MARGIN.top}
                                    x2={maturingBandX}
                                    y2={MARGIN.top + plotHeight}
                                    stroke={hatchLineColor}
                                    strokeWidth={1}
                                    strokeDasharray="3 3"
                                />
                            </g>
                        )}

                        {futureActive && (
                            <g>
                                <rect
                                    x={futureBandX}
                                    y={MARGIN.top}
                                    width={Math.max(0, futureBandRight - futureBandX)}
                                    height={plotHeight}
                                    fill={`url(#${futureId})`}
                                />
                                <line
                                    x1={futureBandX}
                                    y1={MARGIN.top}
                                    x2={futureBandX}
                                    y2={MARGIN.top + plotHeight}
                                    stroke={futureEdgeColor}
                                    strokeWidth={1.5}
                                />
                            </g>
                        )}

                        {yTicks.map((tick, i) => (
                            <g key={`y-${i}`}>
                                <line x1={MARGIN.left} y1={yForTick(tick)} x2={viewWidth - MARGIN.right} y2={yForTick(tick)} stroke={gridColor} strokeWidth={1} />
                                <text x={MARGIN.left - 10} y={yForTick(tick) + 4} textAnchor="end" fontSize={axisFontSize} fontFamily={fontFamily} fill={axisColor}>
                                    {valueFormatter(tick)}
                                </text>
                            </g>
                        ))}

                        {hasRightAxis &&
                            yTicks.map((_, i) => {
                                const tick = (yMaxRight / (yTicks.length - 1)) * i;
                                return (
                                    <text
                                        key={`yr-${i}`}
                                        x={viewWidth - MARGIN.right + 8}
                                        y={yForAxis(tick, "right") + 4}
                                        textAnchor="start"
                                        fontSize={axisFontSize}
                                        fontFamily={fontFamily}
                                        fill={axisColor}
                                    >
                                        {valueFormatter(tick)}
                                    </text>
                                );
                            })}

                        {xTickIndexes.map((idx) => (
                            <text key={`x-${idx}`} x={xFor(idx)} y={viewHeight - MARGIN.bottom + 22} textAnchor="middle" fontSize={axisFontSize} fontFamily={fontFamily} fill={axisColor}>
                                {primary.points[idx]?.label ?? ""}
                            </text>
                        ))}

                        {drawOrder.flatMap((s) =>
                            bandPaths(s).map((d, bi) => (
                                <path
                                    key={`${s.id}-band-${bi}`}
                                    d={d}
                                    fill={alpha(s.color, bandAlpha(s))}
                                    stroke="none"
                                />
                            )),
                        )}

                        {drawOrder.map((s) => (
                            <path
                                key={s.id}
                                d={buildPath(s)}
                                fill="none"
                                stroke={s.fadeFrom ? `url(#${gradientId}-${s.id})` : s.color}
                                strokeWidth={s.dashed ? 2 : 2.75}
                                strokeDasharray={s.tentative ? "3 3" : s.dashed ? "6 6" : undefined}
                                strokeLinejoin="round"
                                strokeLinecap="round"
                                opacity={(s.dashed ? 0.85 : 1) * focusOpacity(s)}
                            />
                        ))}

                        {/* Recolours the stretch of the line that is still settling. Only sound with one
                            line on the chart: with several it repainted whichever happened to be first
                            in grey, hiding the metric the reader had selected. Where predictions are
                            drawn the hatching and the predicted lines already carry the same claim. */}
                        {maturingActive && primary && labelGroups.length === 1 && (
                            <path
                                d={buildPath(primary, maturingStartIndex)}
                                fill="none"
                                stroke={maturingLineColor}
                                strokeWidth={2.75}
                                strokeLinejoin="round"
                                strokeLinecap="round"
                            />
                        )}

                        {hoverIndex != null && (
                            <line
                                x1={xFor(hoverIndex)}
                                y1={MARGIN.top}
                                x2={xFor(hoverIndex)}
                                y2={MARGIN.top + plotHeight}
                                stroke={alpha(theme.palette.text.primary, 0.35)}
                                strokeWidth={1}
                                strokeDasharray="4 4"
                            />
                        )}

                        {hoverIndex != null &&
                            series.map((s) => {
                                const p = s.points[hoverIndex];
                                if (!p || p.value == null || p.silent) return null;
                                return (
                                    <circle key={`dot-${s.id}`} cx={xFor(hoverIndex)} cy={yFor(p.value, s)} r={4.5} fill={s.color} stroke={theme.palette.background.paper} strokeWidth={2} opacity={focusOpacity(s)} />
                                );
                            })}
                    </>
                )}
            </svg>

            {/* Clicking dims every other line, and on a normalised chart also gives the axis real
                numbers for this one; clicking it again returns to comparing all of them. */}
            {onFocusChange && labelGroups.length > 1 && (
                <Stack direction="row" flexWrap="wrap" spacing={1.5} sx={{ px: 0.5, mt: 0.5 }}>
                    {labelGroups.map((s) => (
                        <Stack
                            key={`legend-${s.label}`}
                            direction="row"
                            alignItems="center"
                            spacing={0.75}
                            onClick={() => onFocusChange(focusedId === s.label ? null : s.label)}
                            sx={{
                                cursor: "pointer",
                                py: 0.25,
                                opacity: inFocus(s) ? 1 : 0.45,
                                fontWeight: focusedScale && inFocus(s) ? 700 : 400,
                            }}
                        >
                            <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: s.color }} />
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                {s.label}
                            </Typography>
                        </Stack>
                    ))}
                </Stack>
            )}

            {normalise ? (
                <Typography variant="caption" sx={{ display: "block", mt: 0.5, px: 0.5, color: theme.palette.text.secondary }}>
                    {focusedId
                        ? `Vertical axis reads ${focusedId}. Other lines are drawn to their own scale — compare shape, not height.`
                        : "Each line is drawn to its own scale, so units that differ by orders of magnitude stay comparable. Pick one above to put real numbers on the axis."}
                </Typography>
            ) : (
                focusedId && (
                    <Typography variant="caption" sx={{ display: "block", mt: 0.5, px: 0.5, color: theme.palette.text.secondary }}>
                        {`Showing ${focusedId}. Pick it again to bring the others back.`}
                    </Typography>
                )
            )}

            {hoverIndex != null && tooltipRows.length > 0 && (
                <Box
                    sx={{
                        position: "absolute",
                        top: 8,
                        [tooltipOnRight ? "left" : "right"]: tooltipOnRight ? `calc(${hoverLeftPct}% + 12px)` : `calc(${100 - hoverLeftPct}% + 12px)`,
                        minWidth: 180,
                        p: 1.25,
                        borderRadius: 2,
                        bgcolor: theme.palette.background.paper,
                        boxShadow: 6,
                        border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                        pointerEvents: "none",
                        zIndex: 2,
                    }}
                >
                    <Typography variant="caption" sx={{ fontWeight: 700, color: theme.palette.text.secondary }}>
                        {primary.points[hoverIndex]?.tooltipLabel ?? primary.points[hoverIndex]?.label}
                    </Typography>
                    <Stack spacing={0.5} sx={{ mt: 0.75 }}>
                        {tooltipRows.map((s) => {
                            const p = s.points[hoverIndex];
                            return (
                                <Stack
                                    key={`tt-${s.id}`}
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    spacing={1.5}
                                    sx={{ opacity: inFocus(s) ? 1 : 0.55 }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={0.75}>
                                        <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: s.color, border: s.dashed ? `2px dashed ${s.color}` : undefined }} />
                                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                            {s.label}
                                            {p?.label && p.label !== primary.points[hoverIndex]?.label ? ` (${p.label})` : ""}
                                        </Typography>
                                    </Stack>
                                    <Stack direction="row" alignItems="baseline" spacing={0.5}>
                                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                            {p && p.value != null ? valueFormatter(p.value) : "—"}
                                        </Typography>
                                        {p?.value != null && p.note && (
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: p.noteColor || theme.palette.text.disabled,
                                                    fontWeight: p.noteColor ? 700 : undefined,
                                                }}
                                            >
                                                {p.note}
                                            </Typography>
                                        )}
                                    </Stack>
                                </Stack>
                            );
                        })}
                    </Stack>
                </Box>
            )}

            {futureActive && (
                <Typography variant="caption" sx={{ display: "block", mt: 1, px: 0.5, color: theme.palette.text.secondary }}>
                    <Box
                        component="span"
                        aria-hidden
                        sx={{
                            display: "inline-block",
                            verticalAlign: "middle",
                            width: 11,
                            height: 11,
                            mr: 0.75,
                            borderRadius: "2px",
                            borderLeft: `1.5px solid ${futureEdgeColor}`,
                            backgroundImage: `linear-gradient(90deg, ${futureWashColor}, ${futureWashFadedColor})`,
                        }}
                    />
                    <Box component="span" sx={{ verticalAlign: "middle" }}>
                        {futureNote}
                    </Box>
                </Typography>
            )}

            {maturingActive && (

                <Typography variant="caption" sx={{ display: "block", mt: 1, px: 0.5, color: theme.palette.text.secondary }}>
                    <Box
                        component="span"
                        aria-hidden
                        sx={{
                            display: "inline-block",
                            verticalAlign: "middle",
                            width: 11,
                            height: 11,
                            mr: 0.75,
                            borderRadius: "2px",
                            border: `1px solid ${hatchLineColor}`,

                            backgroundImage: `repeating-linear-gradient(45deg, ${hatchLineColor} 0 1.4px, ${hatchFillColor} 1.4px 7px)`,
                        }}
                    />
                    <Box component="span" sx={{ verticalAlign: "middle" }}>
                        {maturingNote}
                    </Box>
                </Typography>
            )}
        </Box>
    );
};

export default LineChart;
